import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Upload, CheckCircle2, Loader2, FileText, Image as ImageIcon, PenLine } from 'lucide-react';
import { toast } from 'sonner';
import SignaturePad from '../components/SignaturePad';
import { TERMS_INTRO, TERMS_SECTIONS, DECLARATION_TEXT } from '../enrollmentTerms';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'full_name', label: 'Full Name', testid: 'enroll-full-name-input' },
  { key: 'guardian_name', label: 'Guardian Name', testid: 'enroll-guardian-name-input' },
  { key: 'dob', label: 'Date of Birth', type: 'date', testid: 'enroll-dob-input' },
  { key: 'nationality', label: 'Nationality', testid: 'enroll-nationality-input' },
  { key: 'passport_number', label: 'Passport Number / Official ID', testid: 'enroll-passport-number-input' },
  { key: 'email', label: 'Email Address', type: 'email', testid: 'enroll-email-input' },
  { key: 'phone', label: 'Phone / WhatsApp Number', type: 'tel', testid: 'enroll-phone-input' },
  { key: 'permanent_address', label: 'Permanent Address', textarea: true, testid: 'enroll-permanent-address-input' },
  { key: 'current_address', label: 'Current Address', textarea: true, testid: 'enroll-current-address-input' },
  { key: 'adc_candidate_number', label: 'ADC Candidate Number', optional: true, testid: 'enroll-adc-candidate-number-input' },
  { key: 'dental_registration_number', label: 'Dental Registration Number', testid: 'enroll-dental-registration-number-input' },
  { key: 'course_batch', label: 'Course Batch / Dates', testid: 'enroll-course-batch-input' },
];

const UPLOADS = [
  { key: 'photo', label: 'Passport-size Photo', accept: 'image/*', hint: 'JPG or PNG, max 10 MB', testid: 'enroll-photo-upload' },
  { key: 'official_id', label: 'Any Official ID', accept: 'image/*,application/pdf', hint: 'Passport, Aadhaar, Driving Licence etc. — image or PDF, max 10 MB', testid: 'enroll-official-id-upload' },
];

const FileUploadCard = ({ config, file, onSelect }) => {
  const [preview, setPreview] = useState(null);
  React.useEffect(() => {
    if (file && file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(null);
  }, [file]);
  return (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-[#1e3a8a] transition-colors" data-testid={`${config.testid}-card`}>
      <Label className="font-semibold text-gray-900 block mb-2">{config.label} <span className="text-red-500">*</span></Label>
      {preview ? (
        <img src={preview} alt={config.label} className="h-32 w-full object-contain rounded bg-gray-50 mb-2" />
      ) : file ? (
        <div className="flex items-center gap-2 bg-gray-50 rounded p-3 mb-2">
          <FileText className="h-6 w-6 text-[#1e3a8a]" />
          <span className="text-sm text-gray-700 truncate">{file.name}</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-gray-400 mb-2">
          <ImageIcon className="h-6 w-6" />
          <span className="text-sm">{config.hint}</span>
        </div>
      )}
      <input
        type="file"
        accept={config.accept}
        onChange={(e) => onSelect(config.key, e.target.files?.[0] || null)}
        className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#1e3a8a] file:text-white file:text-sm file:cursor-pointer hover:file:bg-[#1e3a8a]/90"
        data-testid={config.testid}
      />
    </div>
  );
};

const Enroll = () => {
  const { token } = useParams();
  const [linkState, setLinkState] = useState('checking'); // checking | valid | invalid
  const [form, setForm] = useState({});
  const [files, setFiles] = useState({ photo: null, official_id: null });
  const [signature, setSignature] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/enroll/validate/${token}`)
      .then(() => setLinkState('valid'))
      .catch(() => setLinkState('invalid'));
  }, [token]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setFile = (key, file) => setFiles((prev) => ({ ...prev, [key]: file }));

  const validate = () => {
    for (const f of FIELDS) {
      if (!f.optional && !(form[f.key] || '').trim()) {
        toast.error(`Please fill in: ${f.label}`);
        return false;
      }
    }
    for (const u of UPLOADS) {
      if (!files[u.key]) {
        toast.error(`Please upload: ${u.label}`);
        return false;
      }
      if (files[u.key].size > 10 * 1024 * 1024) {
        toast.error(`${u.label} must be under 10 MB`);
        return false;
      }
    }
    if (!signature) {
      toast.error('Please provide your signature');
      return false;
    }
    if (!agreed) {
      toast.error('Please read and agree to the Terms & Conditions');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('token', token);
      FIELDS.forEach((f) => fd.append(f.key, (form[f.key] || '').trim()));
      fd.append('agreed_terms', 'true');
      fd.append('signature_data', signature);
      fd.append('photo', files.photo);
      fd.append('official_id', files.official_id);
      const res = await axios.post(`${API}/api/enroll`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setSubmitted(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (linkState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="enroll-checking">
        <Loader2 className="h-10 w-10 animate-spin text-[#1e3a8a]" />
      </div>
    );
  }

  if (linkState === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="enroll-invalid-link">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-3">
            <h1 className="text-2xl font-bold text-gray-900">Invalid Enrollment Link</h1>
            <p className="text-gray-600 text-sm">
              This enrollment link is not valid. Please contact Overseas Dental Achievers at
              +91 9914637377 or enquiries@overseasdentalachievers.co.in to receive your private enrollment link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div data-testid="enroll-success-page">
        <section className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] text-white py-16">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-green-300" />
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Application Submitted</h1>
            <p className="text-lg text-gray-100">
              Thank you, {submitted && form.full_name}. Your ADC Part 2 enrollment application has been received.
            </p>
          </div>
        </section>
        <section className="py-10">
          <div className="max-w-2xl mx-auto px-4">
            <Card>
              <CardContent className="pt-6 text-center space-y-4">
                <p className="text-gray-700">
                  Application ID: <span className="font-mono font-semibold" data-testid="enroll-success-id">{submitted.id}</span>
                </p>
                <p className="text-gray-600 text-sm">
                  A branded copy of your enrollment form has been sent to the academy.
                  {(!submitted.emails || submitted.emails.candidate !== false) &&
                    ' A confirmation copy has been emailed to you.'}
                  {' '}Our team will contact you shortly with the next steps.
                </p>
                {submitted.emails && submitted.emails.academy === false && (
                  <div className="bg-red-50 border border-red-300 rounded-lg p-4 text-left" data-testid="enroll-academy-email-warning">
                    <p className="text-red-800 text-sm font-semibold">Delivery to the academy is delayed</p>
                    <p className="text-red-700 text-sm mt-1">
                      Your application is safely saved, but the email copy to the academy could not be sent just now.
                      Please WhatsApp us at +91 9914637377 quoting your Application ID.
                    </p>
                  </div>
                )}
                {submitted.emails && submitted.emails.candidate === false && (
                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 text-left" data-testid="enroll-email-warning">
                    <p className="text-amber-800 text-sm font-semibold">Confirmation email could not be delivered</p>
                    <p className="text-amber-700 text-sm mt-1">
                      The email address entered ({form.email}) appears to be undeliverable. Don't worry — the academy has received your application.
                      Please contact us on WhatsApp at +91 9914637377 if you'd like a copy of your form.
                    </p>
                  </div>
                )}
                <Button asChild className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90" data-testid="enroll-success-home-button">
                  <a href="/">Back to Home</a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="enroll-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] text-white py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-testid="enroll-title">
            ADC Part 2 Course – Enrollment Form
          </h1>
          <p className="text-lg text-gray-100">
            Overseas Dental Achievers — SCO 224,225, Second Floor, Above IndusInd Bank, Sector 40-D, Chandigarh · Phone: 9914637377
          </p>
        </div>
      </section>

      <form onSubmit={handleSubmit} className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">

          {/* Student Information */}
          <section data-testid="enroll-student-info-section">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#1e3a8a] text-white rounded-full flex items-center justify-center text-sm">1</span>
              Student Information
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {FIELDS.map((f) => (
                <div key={f.key} className={f.textarea ? 'md:col-span-2' : ''}>
                  <Label htmlFor={f.key} className="mb-2 block">
                    {f.label} {!f.optional && <span className="text-red-500">*</span>}
                  </Label>
                  {f.textarea ? (
                    <Textarea
                      id={f.key}
                      rows={3}
                      value={form[f.key] || ''}
                      onChange={(e) => setField(f.key, e.target.value)}
                      data-testid={f.testid}
                    />
                  ) : (
                    <Input
                      id={f.key}
                      type={f.type || 'text'}
                      value={form[f.key] || ''}
                      onChange={(e) => setField(f.key, e.target.value)}
                      data-testid={f.testid}
                    />
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Document Uploads */}
          <section data-testid="enroll-documents-section">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#1e3a8a] text-white rounded-full flex items-center justify-center text-sm">2</span>
              <Upload className="h-6 w-6 text-[#1e3a8a]" /> Document Uploads
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {UPLOADS.map((u) => (
                <FileUploadCard key={u.key} config={u} file={files[u.key]} onSelect={setFile} />
              ))}
            </div>
          </section>

          {/* Signature */}
          <section data-testid="enroll-signature-section">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#1e3a8a] text-white rounded-full flex items-center justify-center text-sm">3</span>
              <PenLine className="h-6 w-6 text-[#1e3a8a]" /> Student Signature
            </h2>
            <SignaturePad onChange={setSignature} />
          </section>

          {/* Terms & Conditions */}
          <section data-testid="enroll-terms-section">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#1e3a8a] text-white rounded-full flex items-center justify-center text-sm">4</span>
              Student Declaration & Terms and Conditions
            </h2>
            <div
              className="border border-gray-300 rounded-lg bg-gray-50 p-6 h-80 overflow-y-auto text-sm text-gray-700 space-y-4"
              data-testid="terms-scroll-box"
            >
              <p>{TERMS_INTRO}</p>
              {TERMS_SECTIONS.map((s) => (
                <div key={s.title}>
                  <h3 className="font-semibold text-gray-900 mb-1">{s.title}</h3>
                  <ul className="list-disc pl-5 space-y-1">
                    {s.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                </div>
              ))}
              <p className="font-semibold text-gray-900 border-t pt-4">{DECLARATION_TEXT}</p>
            </div>
            <div className="flex items-start gap-3 mt-4">
              <Checkbox
                id="agree"
                checked={agreed}
                onCheckedChange={(v) => setAgreed(v === true)}
                data-testid="terms-checkbox"
              />
              <Label htmlFor="agree" className="text-gray-800 leading-snug cursor-pointer">
                I have read, understood and agree. <span className="text-red-500">*</span>
              </Label>
            </div>
          </section>

          <Button
            type="submit"
            size="lg"
            disabled={submitting}
            className="w-full bg-[#dc2626] hover:bg-[#dc2626]/90 text-lg py-6"
            data-testid="enroll-submit-button"
          >
            {submitting ? (
              <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Submitting Application…</>
            ) : (
              'Submit Enrollment Application'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Enroll;
